import {
  u3130ComputeExpenseLineAmountFromUnit,
  u3130ExpenseLineUsesUnitCost,
  type U3130ExpenseItem,
  type U3130FindProjectItem,
  type U3130Timesheet,
} from "../U3130TimesheetModel";
import type { U3130TimesheetSubprojectSaveOptions } from "./u3130ValidateHonorariaLines";

function expenseRowIncludeSubProject(row: U3130ExpenseItem, admIncludeSubProjectEnabled: boolean): boolean {
  if (!admIncludeSubProjectEnabled) return false;
  const project = row.project as U3130FindProjectItem | undefined;
  if (!project || !("includeSubProjet" in project)) return false;
  const v = project.includeSubProjet;
  return v === true || (typeof v === "string" && v === "true");
}

export type U3130ExpenseValidationMessages = {
  transactionDate: string;
  project: string;
  subproject: string;
  expenseType: string;
  taxRate: string;
  quantity: string;
  amount: string;
};

function isFiniteNumber(v: unknown): v is number {
  return typeof v === "number" && !Number.isNaN(v) && Number.isFinite(v);
}

/**
 * Erreurs par clé alignées sur les contrôles : calendrier, montants décimaux, quantité entière,
 * autocomplete projet/sous-projet (`.project.id`, `.subproject.id`), combos (`expenseTypeId`, `taxRateId`).
 * Uniquement lorsque la feuille est éditable côté dépenses (`status === "Open"`).
 */
export function validateExpenseLinesForSave(
  data: U3130Timesheet | null | undefined,
  messages: U3130ExpenseValidationMessages,
  options?: U3130TimesheetSubprojectSaveOptions,
): Record<string, string> {
  if (!data || data.status !== "Open") return {};
  const errors: Record<string, string> = {};
  const admIncludeSubProjectEnabled = options?.admIncludeSubProjectEnabled ?? true;
  const list = data.expenses ?? [];
  list.forEach((row, i) => {
    const base = `expenses.${i}`;
    const dateRaw = row.transactionDate;
    if (dateRaw == null || String(dateRaw).trim() === "") {
      errors[`${base}.transactionDate`] = messages.transactionDate;
    }
    const projectId = row.projectId?.trim() || row.project?.id?.trim() || "";
    if (!projectId) {
      errors[`${base}.project.id`] = messages.project;
    }
    if (expenseRowIncludeSubProject(row, admIncludeSubProjectEnabled)) {
      const sub = row as U3130ExpenseItem & { subproject?: { id?: string } };
      const subId = row.subprojectId?.trim() || sub.subproject?.id?.trim() || "";
      if (!subId) {
        errors[`${base}.subproject.id`] = messages.subproject;
      }
    }
    if (!row.expenseTypeId?.trim()) {
      errors[`${base}.expenseTypeId`] = messages.expenseType;
    }
    if (!row.taxRateId?.trim()) {
      errors[`${base}.taxRateId`] = messages.taxRate;
    }
    const usesUnit = u3130ExpenseLineUsesUnitCost(row);
    if (usesUnit) {
      if (!isFiniteNumber(row.quantity) || !Number.isInteger(row.quantity) || row.quantity < 1) {
        errors[`${base}.quantity`] = messages.quantity;
      } else {
        const expected = u3130ComputeExpenseLineAmountFromUnit(row);
        const amt = row.amount;
        if (!isFiniteNumber(amt) || Math.round(amt * 100) !== Math.round(expected * 100)) {
          errors[`${base}.amount`] = messages.amount;
        }
      }
    } else if (!isFiniteNumber(row.amount) || row.amount <= 0) {
      errors[`${base}.amount`] = messages.amount;
    }
  });
  return errors;
}

export function expensesTabHasValidationErrors(errors: Record<string, string>): boolean {
  return Object.keys(errors).some((k) => k.startsWith("expenses."));
}
